import React, { useEffect, useRef, useState } from 'react'
import styles from '../ProductActivityPage.module.css'
import pv from './Overlays.module.css'
import { apiJson } from '../../lib/api'
import { addDaysLocal, isoLocal, MONS, parseIsoLocal, todayLocal } from '../timesheets/time'
import { DOT, activityLevel, dayLabel } from './model'
import { usePickerDismiss } from '../../components/pickerDismiss'

// The date-period control Product Activity and Batches share (V4 paCal): a
// button that opens two months with activity dots, four presets and Apply.
// Batches reads the same period (§7k.4: "the date period shared with Product
// Activity"), so the dots are the same count of cycles per day. The
// Dashboard (§7) uses the same control with its own presets, dots and
// legend; left out, each is Product Activity's.

const DOW_HEAD = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

export type Level = 0 | 1 | 2 | 3
export type Preset = [label: string, from: string, to: string]

function activityPresets(today: string): Preset[] {
  const dow = (parseIsoLocal(today).getDay() + 6) % 7
  return [
    ['Today', today, today],
    ['Yesterday', addDaysLocal(today, -1), addDaysLocal(today, -1)],
    ['Last 3 days', addDaysLocal(today, -2), today],
    ['This week', addDaysLocal(today, -dow), today],
  ]
}

async function activityLevels(user: NonNullable<Props['user']>, from: string, to: string): Promise<Record<string, Level>> {
  const r = await apiJson<{ days: { work_date: string; cycles: number }[] }>(user, `/api/product-activity/days?from=${from}&to=${to}`)
  const m: Record<string, Level> = {}
  for (const d of r.days || []) m[d.work_date] = activityLevel(d.cycles) as Level
  return m
}

const ACTIVITY_LEGEND: [Level, string][] = [
  [3, 'Busy day'],
  [2, 'Some picking'],
  [1, 'Quiet'],
]

export function datesBetween(from: string, to: string): string[] {
  const out: string[] = []
  for (let d = from; d <= to && out.length < 400; d = addDaysLocal(d, 1)) out.push(d)
  return out
}

export function rangeLabel(from: string, to: string) {
  return from === to ? dayLabel(from) : dayLabel(from) + ' – ' + dayLabel(to)
}

function nextMonth(ym: string, n: number) {
  const d = parseIsoLocal(ym + '-01')
  d.setMonth(d.getMonth() + n)
  return isoLocal(d).slice(0, 7)
}

function lastOfMonth(ym: string) {
  const d = parseIsoLocal(ym + '-01')
  d.setMonth(d.getMonth() + 1)
  d.setDate(0)
  return isoLocal(d)
}

interface Props {
  user: { getIdToken: () => Promise<string>; uid?: string } | null
  from: string
  to: string
  onApply: (from: string, to: string) => void
  presets?: (today: string) => Preset[]
  loadLevels?: (user: NonNullable<Props['user']>, from: string, to: string) => Promise<Record<string, Level>>
  legend?: [Level, string][]
  // The dot on a range's first and last day, which sit on the accent.
  edgeDot?: string
}

export function DatePeriod({ user, from, to, onApply, presets: presetsFor = activityPresets, loadLevels = activityLevels, legend = ACTIVITY_LEGEND, edgeDot = '#fff' }: Props) {
  const today = todayLocal()
  const [calOpen, setCalOpen] = useState(false)
  const [draft, setDraft] = useState<{ from: string; to: string | null }>({ from, to })
  const [calMonth, setCalMonth] = useState(() => nextMonth(to.slice(0, 7), -1))
  const [activity, setActivity] = useState<Record<string, Level>>({})
  const userRef = useRef(user)
  userRef.current = user
  const loadRef = useRef(loadLevels)
  loadRef.current = loadLevels
  const uid = user?.uid
  // Months whose dots are already read (h 526-527: cache per month). The
  // month holding today is never kept: its days are still being picked.
  const fetched = useRef(new Set<string>())
  useEffect(() => {
    fetched.current.clear()
    setActivity({})
  }, [uid])

  // The calendar's dots over the two months in view, reading only the months
  // not already held: paging back and forth, or reopening, costs nothing.
  useEffect(() => {
    const u = userRef.current
    if (!calOpen || !u) return
    const want = [calMonth, nextMonth(calMonth, 1)].filter((ym) => !fetched.current.has(ym))
    if (!want.length) return
    let live = true
    loadRef
      .current(u, `${want[0]}-01`, lastOfMonth(want[want.length - 1]))
      .then((m) => {
        if (!live) return
        for (const ym of want) if (ym !== today.slice(0, 7)) fetched.current.add(ym)
        setActivity((a) => ({ ...a, ...m }))
      })
      .catch(() => {
        /* dots are a hint; the calendar still works without them */
      })
    return () => {
      live = false
    }
  }, [calOpen, calMonth, uid, today])

  const close = () => setCalOpen(false)
  const wrap = usePickerDismiss(calOpen, close)
  const openCal = () => {
    if (calOpen) return setCalOpen(false)
    setDraft({ from, to })
    setCalMonth(nextMonth(to.slice(0, 7), -1))
    setCalOpen(true)
  }
  const applyCal = () => {
    onApply(draft.from, draft.to || draft.from)
    setCalOpen(false)
  }
  // First click anchors, a second on or after it closes the range, an earlier
  // one starts again from there (V4 paPickDay).
  const pickDay = (iso: string) => {
    setDraft((d) => (d.to === null && iso >= d.from ? { from: d.from, to: iso } : { from: iso, to: null }))
  }
  const presets = presetsFor(today)
  const days = datesBetween(from, to).length

  return (
    <>
      <div className={styles.calWrap} ref={wrap}>
        <button type="button" className={styles.dateBtn} onClick={openCal} aria-expanded={calOpen}>
          <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span>Date period</span>
          <span className={styles.dateRange}>{rangeLabel(from, to)}</span>
        </button>
        {calOpen && (
          <div className={styles.calendar} role="dialog" aria-label="Date period">
            <div className={styles.rangeFields}>
              <div>
                <div className={styles.rangeLabel}>From</div>
                <div className={draft.to === null ? pv.rangeBox : pv.rangeBoxOn}>{dayLabel(draft.from)}</div>
              </div>
              <div>
                <div className={styles.rangeLabel}>To</div>
                <div className={draft.to === null ? pv.rangeBoxOn : pv.rangeBox}>{draft.to === null ? 'Pick the last day' : dayLabel(draft.to)}</div>
              </div>
            </div>
            <div className={styles.months}>
              {[calMonth, nextMonth(calMonth, 1)].map((ym, i) => (
                <Month
                  key={ym}
                  ym={ym}
                  today={today}
                  draft={draft}
                  activity={activity}
                  edgeDot={edgeDot}
                  onPick={pickDay}
                  nav={i === 0 ? { label: 'Earlier months', onClick: () => setCalMonth(nextMonth(calMonth, -1)) } : { label: 'Later months', onClick: () => setCalMonth(nextMonth(calMonth, 1)) }}
                  navSide={i === 0 ? 'left' : 'right'}
                />
              ))}
            </div>
            <div className={styles.legend}>
              {legend.map(([lvl, label]) => (
                <span key={lvl}>
                  <i style={{ background: DOT[lvl as 1 | 2 | 3] }} />
                  {label}
                </span>
              ))}
              <span>No dot: no activity recorded</span>
            </div>
            <div className={styles.presets}>
              {presets.map(([label, f, t]) => (
                <button key={label} type="button" className={draft.from === f && draft.to === t ? pv.presetOn : pv.preset} onClick={() => setDraft({ from: f, to: t })}>
                  {label}
                </button>
              ))}
              <button type="button" className={styles.applyBtn} onClick={applyCal}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
      <span className={styles.calNote}>
        {days} day{days === 1 ? '' : 's'} selected
      </span>
    </>
  )
}

function Month({
  ym,
  today,
  draft,
  activity,
  edgeDot,
  onPick,
  nav,
  navSide,
}: {
  ym: string
  today: string
  draft: { from: string; to: string | null }
  activity: Record<string, Level>
  edgeDot: string
  onPick: (iso: string) => void
  nav: { label: string; onClick: () => void }
  navSide: 'left' | 'right'
}) {
  const first = parseIsoLocal(ym + '-01')
  const lead = (first.getDay() + 6) % 7
  const last = parseIsoLocal(lastOfMonth(ym)).getDate()
  const cells: (string | null)[] = Array.from({ length: lead }, () => null)
  for (let d = 1; d <= last; d++) cells.push(ym + '-' + String(d).padStart(2, '0'))
  const lo = draft.from
  const hi = draft.to ?? draft.from
  const navBtn = (
    <button type="button" className={styles.monthNav} onClick={nav.onClick} aria-label={nav.label}>
      {navSide === 'left' ? '‹' : '›'}
    </button>
  )
  return (
    <div>
      <div className={styles.monthHead}>
        {navSide === 'left' && navBtn}
        <span>
          {MONS[first.getMonth()]} {first.getFullYear()}
        </span>
        {navSide === 'right' && navBtn}
      </div>
      <div className={styles.monthGrid}>
        {DOW_HEAD.map((d, i) => (
          <div key={i} className={styles.dow}>
            {d}
          </div>
        ))}
        {cells.map((iso, i) => {
          if (!iso) return <div key={'x' + i} />
          const inRange = iso >= lo && iso <= hi
          const edge = iso === lo || iso === hi
          const lvl = activity[iso] || 0
          const cls = [styles.day, inRange ? styles.dayIn : '', edge ? styles.dayEdge : '', iso === today ? styles.dayToday : '', iso > today ? styles.dayFuture : ''].join(' ')
          return (
            <button key={iso} type="button" className={cls} onClick={() => onPick(iso)} aria-label={dayLabel(iso)} aria-pressed={inRange}>
              <span>{parseIsoLocal(iso).getDate()}</span>
              <span className={styles.dayDot} style={lvl ? { background: edge ? edgeDot : DOT[lvl] } : undefined} />
            </button>
          )
        })}
      </div>
    </div>
  )
}
